import { useCallback, useContext, useEffect, useRef, useState } from "react";

import {
   getAdminRatingsAll,
   getAdminRatingsNewCount,
} from "../api/instructorRatingsService";
import { canViewAdminReviews } from "../auth/reviewsAccess";
import {
   countUnreadAdminReviews,
   getAdminReviewsLastSeenAt,
} from "../utils/adminReviewsSeen";
import {
   getCurrentPopup,
   subscribePopup,
} from "../components/Common/popupUIStore";
import { UserContext } from "../UserContext";

// Focus, visibilitychange si schimbarile de popup reincarca badge-ul cel mult o
// data pe minut. Badge-ul cere doar numarul (/instructor-ratings/admin/count);
// pe un server fara ruta (404) — toate recenziile (/admin/all), ca inainte. Un declansator venit in acest minut se
// ignora si nu se reia: badge-ul se actualizeaza la primul focus/popup venit
// la cel putin 60 s dupa ultima reincarcare. Incarcarea la montare, 0 la
// deschiderea Recenziilor si reincarcarea la inchiderea lor raman imediate.
const REFRESH_THROTTLE_MS = 60_000;

// `instructor` vine de la apelant (Header il rezolva deja pentru meniu), ca sa
// nu mai cerem /instructors a doua oara doar pentru badge.
export default function useAdminReviewsUnread(instructor = null) {
   const { user, loading } = useContext(UserContext);
   const canAccess = canViewAdminReviews(user, instructor);
   const [unreadCount, setUnreadCount] = useState(0);
   const [ratings, setRatings] = useState([]);
   // serverul fără /admin/count (404): rămânem pe lista întreagă, ca înainte
   const countRouteMissingRef = useRef(false);

   const refresh = useCallback(async () => {
      if (!canAccess) {
         setUnreadCount(0);
         setRatings([]);
         return [];
      }

      try {
         // doar numărul celor venite după ultima vizită în „Recenzii” (~50 B în loc de lista întreagă, ~1,3 MB)
         if (!countRouteMissingRef.current) {
            const count = await getAdminRatingsNewCount(
               getAdminReviewsLastSeenAt(user?.id),
            );
            if (count !== null) {
               setUnreadCount(count);
               return [];
            }
            countRouteMissingRef.current = true;
         }
         const rows = await getAdminRatingsAll();
         const list = Array.isArray(rows) ? rows : [];
         setRatings(list);
         setUnreadCount(countUnreadAdminReviews(list, user?.id));
         return list;
      } catch {
         setUnreadCount(0);
         setRatings([]);
         return [];
      }
   }, [canAccess, user?.id]);

   const lastRefreshAtRef = useRef(0);
   const refreshNow = useCallback(() => {
      lastRefreshAtRef.current = Date.now();
      refresh();
   }, [refresh]);
   const refreshThrottled = useCallback(() => {
      const elapsed = Date.now() - lastRefreshAtRef.current;
      // elapsed < 0: ceasul a fost dat inapoi; nu blocam reincarcarile
      if (elapsed >= 0 && elapsed < REFRESH_THROTTLE_MS) return;
      refreshNow();
   }, [refreshNow]);

   useEffect(() => {
      if (loading || !user || !canAccess) return undefined;
      refresh();
      return undefined;
   }, [canAccess, loading, refresh, user]);

   useEffect(() => {
      if (!canAccess) return undefined;

      const handleFocus = () => {
         refreshThrottled();
      };

      const handleVisibility = () => {
         if (document.visibilityState === "visible") {
            refreshThrottled();
         }
      };

      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);

      return () => {
         window.removeEventListener("focus", handleFocus);
         document.removeEventListener("visibilitychange", handleVisibility);
      };
   }, [canAccess, refreshThrottled]);

   useEffect(() => {
      if (!canAccess) return undefined;

      let reviewsOpen = getCurrentPopup()?.type === "adminReviews";
      return subscribePopup(() => {
         const current = getCurrentPopup();
         if (current?.type === "adminReviews") {
            reviewsOpen = true;
            setUnreadCount(0);
            return;
         }
         if (reviewsOpen) {
            // La inchiderea Recenziilor badge-ul se reface imediat, ca inainte:
            // o reincarcare pornita inainte de deschidere poate aduce numarul vechi.
            reviewsOpen = false;
            refreshNow();
            return;
         }
         refreshThrottled();
      });
   }, [canAccess, refreshNow, refreshThrottled]);

   return {
      canAccess,
      unreadCount,
      ratings,
      refresh,
   };
}
